import { stripWorksExport } from "@utils/worksReceivables";
import { useProjectActions, useProjectContext } from "@contexts/projects";
import { downloadCsv } from "@utils/downloadCsv";
import { ensureDateType } from "@utils/formatDate";
import { loadStorage, saveStorage } from "@utils/localStorageManager";
import { useCallback, useEffect, useState } from "react";
import { isCashFlowRawExportVisible } from "./financialProjection/cashFlowPlanning";

const formatDateToInput = (value) => {
  const date = ensureDateType(value);
  return date.toISOString().slice(0, 7);
}

const formatInputToDate = (value) => {
  const [year, month] = value.split("-");
  return new Date(Date.UTC(year, month - 1, 1))
}

const monthYearToIso = (value) => {
  if (typeof value !== "string") return null;
  const [month, year] = value.split("/");
  if (!month || !year) return null;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, 1));
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

const DateRangeFilter = () => {
  const { activeProject = {}, filterDates, xlsxData = {}, selectedGraph, filterDocs = {} } = useProjectContext();
  const {
    _id: projectId,
    start_date,
    end_date,
    lastPayment,
    firstPayment,
  } = activeProject;
  const { fields = [], resources = [] } = stripWorksExport(xlsxData);
  const { changeDates, getReport, getAllBuildingItems, updateMaxDates } = useProjectActions();

  const [showReportButtons, setShowReportButtons] = useState(false);
  const [savedDates, setSavedDates] = useState({})
  const [reportError, setReportError] = useState(false);

  const lastDate = monthYearToIso(lastPayment?.date);
  const firstDate = monthYearToIso(firstPayment) || start_date;

  const availableStartDates = [
    firstDate,
    start_date
  ].filter(Boolean);
  const olderDate = availableStartDates.reduce((earliest, current) =>
    new Date(current) < new Date(earliest) ? current : earliest, availableStartDates[0]);
  const hasDateRange = Boolean(projectId && start_date && end_date && lastDate && olderDate && filterDates?.beginAt && filterDates?.endAt);

  const changeStart = useCallback(({ target: { value } }) => {
    if (value.length <= 0) return;
    const startDate = formatInputToDate(value);
    if (filterDocs.paidData.length > 0) {
    }
    saveStorage(`${projectId}_filterDates`, {
      beginAt: startDate,
      endAt: filterDates.endAt
    })
    changeDates(projectId, startDate, filterDates.endAt)
  }, [projectId, filterDates.endAt])

  const changeEnd = useCallback(({ target: { value } }) => {
    if (value.length <= 0) return;
    const endDate = formatInputToDate(value);
    saveStorage(`${projectId}_filterDates`, {
      beginAt: filterDates.beginAt,
      endAt: endDate
    })
    changeDates(projectId, filterDates.beginAt, endDate)
  }, [projectId, filterDates.beginAt])


  useEffect(() => {
    if (!hasDateRange) return;
    updateMaxDates(olderDate, lastDate)
    const saved = loadStorage(`${projectId}_filterDates`);
    if (saved) {
      setSavedDates(saved);
      changeDates(projectId, new Date(saved.beginAt), new Date(saved.endAt));
    }
  }, [projectId])

  const handleDownload = async (e) => {
    const col = e.target.name;
    e.preventDefault()

    if (col === 'all') {
      simpleDownload(e, col)
      return;
    }

    setReportError(false);
    const res = await getReport(col);
    if (!res || res.errorCode || res.code >= 400) {
      setReportError(true);
      return;
    }

    if (col === 'advancements') {
      let items = []
      res.items.forEach(i => {
        i.data.forEach(j => {
          items.push({
            from: j.from,
            to: j.to,
            value: j.value,
            name: i.name
          })
        })
      })

      const fields = [
        { key: 'name', label: "Nome" },
        { key: 'value', label: "Valor" },
        { key: 'from', label: "Movido de" },
        { key: 'to', label: "Movido para" },
      ]

      downloadCsv(e, fields, items, col);
      return
    }

    const allItems = await getAllBuildingItems(['units', 'cells', 'subcells', 'steps', 'substeps', 'services']);

    const dates = res.items.map(i => i.data.map(j => `${j.month}/${j.year}`));

    const eaps = res.items.map(i => i.wbs)

    const uniqueDates = [...new Set(dates.flat())]

    const formated = res.items.map(i => {
      let temp = { ...i };
      const { data } = i;
      uniqueDates.forEach(j => {
        const item = data.find(k => `${k.month}/${k.year}` === j);
        temp[j] = item ? item.value : 0;
      });
      const item = allItems.items.find(j => i.wbs === j.wbs_code_id)
      if (!item) return temp;
      temp.description = item.description || item.building_unit_name
      delete temp.data
      return temp;
    });

    const orderedFormated = formated.sort((a, b) => a.wbs.localeCompare(b.wbs));

    let nFields = [
      { key: 'wbs', label: "EAP" },
      { key: 'description', label: "Nome" },
    ]

    const sortedUniqueDates = uniqueDates.sort((a, b) => {
      const [monthA, yearA] = a.split("/").map(Number);
      const [monthB, yearB] = b.split("/").map(Number);
      return yearA - yearB || monthA - monthB;
    });

    sortedUniqueDates.forEach(i => {
      nFields.push({ key: i, label: i });
    });

    downloadCsv(e, nFields, orderedFormated, col);
  }

  const simpleDownload = (e, name) => {
    downloadCsv(e, fields, resources, name)
  }

  const handleReportButton = (e) => {
    e.preventDefault()
    let col;
    switch (selectedGraph) {
      case 'fluxGraph':
        col = "Desembolsos acumulados";
        break;
      case 'balanceGraph':
        col = "Compromissos da obra";
        break;

      default:
        break;
    }
    if (selectedGraph === 'fluxGraph' || selectedGraph === 'balanceGraph') {
      simpleDownload(e, col)
      return
    } else {
      setShowReportButtons(!showReportButtons)
    }
  }

  if (!hasDateRange) return null;

  return <form className="date-range">
    <h3>Periodo:</h3>
    <input
      type="month"
      required={true}
      min={formatDateToInput(olderDate)}
      max={formatDateToInput(filterDates.endAt)}
      value={formatDateToInput(filterDates.beginAt)}
      onChange={changeStart}
    />
    <span className="color-gray">-</span>
    <input
      type="month"
      required
      min={formatDateToInput(filterDates.beginAt)}
      max={formatDateToInput(lastDate)}
      value={formatDateToInput(filterDates.endAt)}
      onChange={changeEnd}
    />

    <div>
      <button className="button button-accent small" onClick={handleReportButton}>Baixar relatório</button>
        {reportError ? (
          <p role="alert">
            Não foi possível carregar o relatório de despesas. Tente novamente.
          </p>
        ) : null}
      {showReportButtons && <div className="report-options">
        {isCashFlowRawExportVisible("disbursements", activeProject.cashFlowPlanning) && (
          <button className="button button-secondary small" name="disbursements" onClick={handleDownload}>Desembolso</button>
        )}
        {isCashFlowRawExportVisible("physical_financials", activeProject.cashFlowPlanning) && (
          <button className="button button-secondary small" name="physical_financials" onClick={handleDownload}>Físico Financeiro</button>
        )}
        {isCashFlowRawExportVisible("ampli_disbursements", activeProject.cashFlowPlanning) && (
          <button className="button button-secondary small" name="ampli_disbursements" onClick={handleDownload}>Desembolso (Ampli)</button>
        )}
        <button className="button button-secondary small" name="paid_values" onClick={handleDownload}>Pago</button>
        <button className="button button-secondary small" name="to_pay_values" onClick={handleDownload}>A Pagar</button>
        {isCashFlowRawExportVisible("advancements", activeProject.cashFlowPlanning) && (
          <button className="button button-secondary small" name="advancements" onClick={handleDownload}>Ajustes de desembolso</button>
        )}
        <button className="button button-secondary small" name="all" onClick={handleDownload}>Relatório Geral</button>
      </div>}
    </div>
  </form>
};

export default DateRangeFilter;
